import { useEffect, useRef } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faExclamationCircle,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { JsonApiInludedRelationship } from "src/api/jsonApiTypes";
import { CaseDetailsBlock } from "src/cases/caseView/blocks/CaseDetailsBlock";
import { EntityDetailsBlock } from "src/cases/caseView/blocks/EntityDetailsBlock";
import { EventDetailsBlock } from "src/cases/caseView/blocks/EventDetailsBlock";
import { MarkdownBlock } from "src/cases/caseView/blocks/MarkdownBlock";
import { RelatedCasesBlock } from "src/cases/caseView/blocks/RelatedCasesBlock";
import { RelatedEntitiesBlock } from "src/cases/caseView/blocks/RelatedEntitiesBlock";
import { RelatedEventsBlock } from "src/cases/caseView/blocks/RelatedEventsBlock";
import { TableBlock } from "src/cases/caseView/blocks/TableBlock";
import { CaseBlock, CaseBlockPosition } from "src/cases/caseView/CaseBlock";
import {
  useActiveBlock,
  useCaseBlockScrollTo,
  useUnsetLastFocusedBlock,
} from "src/cases/caseView/CaseBlockScrollContext";
import {
  CaseBlockMarkdown,
  CaseDeserialized,
  TableBlockRecord,
} from "src/cases/queries/casesTypes";
import { getBlockIcon } from "src/cases/templates/caseBlocks/blockIcons";
import {
  CaseBlockConfigOutput,
  CaseEnvironment,
  Config1,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { useEventListener } from "src/hooks/usehooks-ts";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { logger } from "src/utils/logger";
import { assertUnreachable } from "src/utils/typeUtils";

type CaseBlocksProps = {
  blocks: CaseDeserialized["data"]["case_blocks"];
  blocksConfig: CaseDeserialized["data"]["template_version"]["case_blocks_configs"];
  caseEnv: CaseEnvironment;
  caseData?: CaseDeserialized;
};

const BlockErrorFallback: React.FC<{
  title: string;
  icon?: IconDefinition | null;
  position: CaseBlockPosition;
  blockKey: string;
  resetErrorBoundary: () => void;
}> = ({ title, icon, position, blockKey, resetErrorBoundary }) => (
  <CaseBlock blockKey={blockKey} icon={icon} position={position} title={title}>
    <div className="flex flex-1 flex-col items-center justify-center">
      <EmptyState
        actionButton={{
          icon: faRefresh,
          label: "Retry",
          onClick: resetErrorBoundary,
        }}
        description="We're having trouble loading this section. Please try again or contact support if the problem persists."
        headline="Something went wrong"
        icon={faExclamationCircle}
        size="compact"
        variant="error"
      />
    </div>
  </CaseBlock>
);

type CaseBlockScrollButtonProps = {
  onClick: () => void;
  isActive: boolean;
  children: React.ReactNode;
};

const CaseBlockScrollButton: React.FC<CaseBlockScrollButtonProps> = ({
  onClick,
  isActive,
  children,
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (ref.current && isActive) {
      ref.current.scrollIntoView({ behavior: "smooth", inline: "nearest" });
    }
  }, [isActive]);

  return (
    <button
      ref={ref}
      className={twJoin(
        "relative h-full px-3 py-2 font-inter-semibold-13px text-gray-800 transition-colors",
        "focus:outline-none",
        "after:absolute after:right-0 after:bottom-0 after:left-0 after:h-[3px]",
        "after:rounded-full after:bg-indigo-500 after:opacity-0",
        "after:transition-opacity after:duration-300",
        "data-[selected=true]:after:opacity-100",
        "text-nowrap",
      )}
      data-selected={isActive}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const renderBlockByType = (
  config: CaseBlockConfigOutput,
  block: CaseDeserialized["data"]["case_blocks"][number],
  blockKey: string,
  caseEnv: CaseEnvironment,
  caseData?: CaseDeserialized,
) => {
  const icon = getBlockIcon(config.icon);
  const baseProps = {
    title: config.title,
    icon,
    position: config.case_block_positioning,
    blockKey,
  };

  switch (block.kind) {
    case "markdown": {
      const markdownBlockData = block as unknown as JsonApiInludedRelationship<
        CaseBlockMarkdown,
        "case_blocks"
      >;
      return (
        <MarkdownBlock
          key={block.id}
          {...baseProps}
          data={markdownBlockData.data}
        />
      );
    }
    case "table": {
      const tableConfig = config.config as Extract<Config1, { kind: "table" }>;
      return (
        <TableBlock
          key={block.id}
          {...baseProps}
          caseData={caseData}
          data={block.data as { record: TableBlockRecord }}
          config={tableConfig}
        />
      );
    }
    case "case_details": {
      return (
        <CaseDetailsBlock
          key={block.id}
          {...baseProps}
          caseEnv={caseEnv}
          caseId={block.config.case_id}
        />
      );
    }
    case "entity_details": {
      return (
        <EntityDetailsBlock
          key={block.id}
          {...baseProps}
          caseEnv={caseEnv}
          data={block.config}
        />
      );
    }
    case "event_details": {
      return (
        <EventDetailsBlock
          key={block.id}
          {...baseProps}
          caseEnv={caseEnv}
          data={block.config}
        />
      );
    }
    case "related_events": {
      return (
        <RelatedEventsBlock
          key={block.id}
          {...baseProps}
          caseEnv={caseEnv}
          data={block.config}
        />
      );
    }
    case "related_entities": {
      return (
        <RelatedEntitiesBlock
          key={block.id}
          {...baseProps}
          caseEnv={caseEnv}
          data={{
            entity: block.config.entity,
            pinned_entities: block.config.pinned_entities,
          }}
        />
      );
    }
    case "related_cases": {
      // Extract specific block config type
      type RelatedCasesBlockConfig = Extract<
        Config1,
        { kind: "related_cases" }
      >;
      const blockConfig = config.config as RelatedCasesBlockConfig;

      if (!blockConfig.entity.key) {
        logger.error("Related cases block has no entity key", { blockConfig });
        return null;
      }

      return (
        <RelatedCasesBlock
          key={block.id}
          {...baseProps}
          caseEnv={caseEnv}
          data={block.config}
        />
      );
    }

    default:
      assertUnreachable(block);
  }
};

export const CaseBlocks: React.FC<CaseBlocksProps> = ({
  blocks,
  blocksConfig,
  caseEnv,
  caseData,
}) => {
  const sortedBlocksConfig = sortBlockConfigsByPosition(blocksConfig);
  const scrollToBlock = useCaseBlockScrollTo();
  const activeBlock = useActiveBlock();
  const unsetLastFocusedBlock = useUnsetLastFocusedBlock();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEventListener(
    "wheel",
    (event: WheelEvent) => {
      if (event.deltaY && Math.abs(event.deltaY) > 35) {
        unsetLastFocusedBlock();
      }
    },
    scrollContainerRef,
  );

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className="scrollbar-hide flex h-12 w-full flex-none items-end gap-4 overflow-x-auto border-gray-200 border-b bg-white px-4"
        data-loc="case-blocks-header"
      >
        {sortedBlocksConfig.map(({ key, config }) => (
          <CaseBlockScrollButton
            key={key}
            isActive={activeBlock === key}
            onClick={() => scrollToBlock(key)}
          >
            {config.title}
          </CaseBlockScrollButton>
        ))}
      </div>
      <div
        ref={scrollContainerRef}
        className="decideScrollbar"
        data-loc="case-blocks-content"
      >
        <div className="grid auto-rows-[100px] grid-cols-6 gap-4 p-4 pb-20">
          {blocks.map((block) => {
            const key = block.id.split("#")[1] ?? block.id;

            if (!blocksConfig[key]) {
              return null;
            }

            const config = blocksConfig[key];
            const icon = getBlockIcon(config.icon);

            return (
              <ErrorBoundary
                key={block.id}
                fallbackErrorContent={({ resetErrorBoundary }) => (
                  <BlockErrorFallback
                    blockKey={key}
                    icon={icon}
                    position={config.case_block_positioning}
                    resetErrorBoundary={resetErrorBoundary}
                    title={config.title}
                  />
                )}
              >
                {renderBlockByType(config, block, key, caseEnv, caseData)}
              </ErrorBoundary>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Sorts case block configurations by their grid position (top-left to bottom-right)
 * First by row (top to bottom), then by column (left to right)
 */
export const sortBlockConfigsByPosition = (
  blocksConfig: CaseDeserialized["data"]["template_version"]["case_blocks_configs"],
): Array<{ key: string; config: CaseBlockConfigOutput }> => {
  return Object.entries(blocksConfig)
    .map(([key, config]) => ({ key, config }))
    .toSorted((a, b) => {
      const posA = a.config.case_block_positioning;
      const posB = b.config.case_block_positioning;

      // Sort by row first (top to bottom)
      if (posA.row !== posB.row) {
        return posA.row - posB.row;
      }

      // Then by column (left to right)
      return posA.column - posB.column;
    });
};
